import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import PostData from "../../services/PostData.js";
import {
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
    emptySelectionModel,
    selectionIds,
} from "../Products/catalogGridStyles.js";
import "../Products/catalog.css";

const FILTER_FIELDS = [
    { id: "itemId", label: "Item ID", type: "text", getValue: (row) => row.itemId },
    { id: "shelves", label: "Shelf", type: "text", getValue: (row) => row.shelves },
    { id: "sku", label: "SKU", type: "text", getValue: (row) => row.sku },
    {
        id: "orderNumber",
        label: "Order Number",
        type: "text",
        getValue: (row) => row.orderNumber || row.customerOrderId,
    },
    {
        id: "buyerName",
        label: "Customer",
        type: "text",
        getValue: (row) => row.buyerName,
    },
    {
        id: "sellingChannel",
        label: "Channel",
        type: "select",
        options: ["eBay", "Walmart"],
        getValue: (row) => row.sellingChannel,
    },
    { id: "orderStatus", label: "Order Status", type: "text", getValue: (row) => row.orderStatus },
    { id: "status", label: "Item Status", type: "text", getValue: (row) => row.status },
];

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

function channelChipSx(channel) {
    const key = String(channel || "").toLowerCase();
    if (key === "ebay") {
        return {
            height: 24,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#fef3c7",
            color: "#854d0e",
            border: "1px solid #fcd34d",
        };
    }
    if (key === "walmart") {
        return {
            height: 24,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#dbeafe",
            color: "#1e40af",
            border: "1px solid #93c5fd",
        };
    }
    return {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
        backgroundColor: "#f1f5f9",
        color: "#475569",
    };
}

export default function FulfillmentGrid({ rows, loading, onChanged }) {
    const [searchInput, setSearchInput] = useState("");
    const [selectionModel, setSelectionModel] = useState(() => emptySelectionModel());
    const [marking, setMarking] = useState(false);
    const [actionError, setActionError] = useState("");
    const { apply: applyFilter, filterActive, barProps: filterBar } = useGridFilter(FILTER_FIELDS);
    const selectedIds = useMemo(() => selectionIds(selectionModel), [selectionModel]);

    const filteredRows = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        let list = rows || [];
        if (q) {
            list = list.filter((row) =>
                [
                    row.itemId,
                    row.shelves,
                    row.sku,
                    row.orderNumber,
                    row.customerOrderId,
                    row.buyerName,
                    row.sellingChannel,
                    row.orderStatus,
                    row.itemTitle,
                    row.productName,
                ]
                    .join(" ")
                    .toLowerCase()
                    .includes(q)
            );
        }
        return applyFilter(list);
    }, [rows, searchInput, applyFilter]);

    const markPicked = async (ids) => {
        const list = (ids || []).map(String).filter(Boolean);
        if (!list.length) return;
        setMarking(true);
        setActionError("");
        try {
            const result = await PostData({ ids: list }, "broylesllc/fulfillment/mark-picked");
            if (!result?.success) {
                setActionError(
                    result?.error?.response?.data?.message || "Could not mark items picked."
                );
                return;
            }
            setSelectionModel(emptySelectionModel());
            await onChanged?.();
        } finally {
            setMarking(false);
        }
    };

    const markMissing = async (id) => {
        const catalogId = String(id || "").trim();
        if (!catalogId) return;
        setMarking(true);
        setActionError("");
        try {
            const result = await PostData({ id: catalogId }, "broylesllc/fulfillment/mark-missing");
            if (!result?.success) {
                setActionError(
                    result?.error?.response?.data?.message || "Could not mark item missing."
                );
                return;
            }
            const payload = result?.data || {};
            if (!payload.replaced) {
                window.alert(
                    payload.message ||
                        "Item marked Missing, but no Listed replacement was available for this order."
                );
            } else {
                setActionError("");
            }
            setSelectionModel(emptySelectionModel());
            await onChanged?.();
        } finally {
            setMarking(false);
        }
    };

    const layout = useGridLayout(GRID_LAYOUT_KEYS.fulfillment, {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = [
        {
            field: "picked",
            headerName: "Picked",
            width: 100,
            sortable: false,
            filterable: false,
            hideable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Button
                    size="small"
                    disabled={marking}
                    onClick={(event) => {
                        event.stopPropagation();
                        markPicked([params.row.id]);
                    }}
                    sx={{
                        ...catalogPrimaryButtonSx,
                        minWidth: 0,
                        px: 1.25,
                        py: 0.25,
                        fontSize: 12,
                    }}
                >
                    Picked
                </Button>
            ),
        },
        {
            field: "missing",
            headerName: "Missing",
            width: 110,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Button
                    size="small"
                    disabled={marking}
                    onClick={(event) => {
                        event.stopPropagation();
                        markMissing(params.row.id);
                    }}
                    sx={{
                        ...catalogQuietButtonSx,
                        minWidth: 0,
                        px: 1.25,
                        py: 0.25,
                        fontSize: 12,
                        color: "#b45309",
                        borderColor: "#fcd34d",
                        backgroundColor: "#fffbeb",
                        "&:hover": {
                            borderColor: "#f59e0b",
                            backgroundColor: "#fef3c7",
                        },
                    }}
                >
                    Missing
                </Button>
            ),
        },
        {
            field: "itemId",
            headerName: "Item ID",
            minWidth: 150,
            flex: 0.7,
            renderCell: (params) => (
                <Link
                    component={RouterLink}
                    to={`/catalog-items?q=${encodeURIComponent(params.value || "")}`}
                    underline="hover"
                    sx={{ color: "#0f766e", fontWeight: 600 }}
                >
                    {params.value || "—"}
                </Link>
            ),
        },
        {
            field: "shelves",
            headerName: "Shelf",
            minWidth: 140,
            flex: 0.7,
            valueGetter: (_value, row) => row.shelves || "—",
        },
        {
            field: "sku",
            headerName: "SKU",
            minWidth: 140,
            flex: 0.7,
        },
        {
            field: "orderNumber",
            headerName: "Order",
            minWidth: 160,
            flex: 0.8,
            renderCell: (params) => {
                const orderNumber = params.row.orderNumber || params.row.customerOrderId;
                if (!orderNumber) return "—";
                return (
                    <Link
                        component={RouterLink}
                        to={`/orders?order=${encodeURIComponent(orderNumber)}`}
                        underline="hover"
                        sx={{ color: "#0f766e", fontWeight: 600 }}
                    >
                        {orderNumber}
                    </Link>
                );
            },
        },
        {
            field: "buyerName",
            headerName: "Customer",
            minWidth: 160,
            flex: 0.8,
            valueGetter: (_value, row) => row.buyerName || "—",
        },
        {
            field: "sellingChannel",
            headerName: "Channel",
            minWidth: 110,
            flex: 0.45,
            renderCell: (params) =>
                params.value ? (
                    <Chip size="small" label={params.value} sx={channelChipSx(params.value)} />
                ) : (
                    "—"
                ),
        },
        {
            field: "orderStatus",
            headerName: "Order Status",
            minWidth: 130,
            flex: 0.55,
        },
        {
            field: "status",
            headerName: "Item Status",
            minWidth: 110,
            flex: 0.45,
        },
        {
            field: "itemTitle",
            headerName: "Title",
            minWidth: 200,
            flex: 1.2,
            valueGetter: (_value, row) => row.itemTitle || row.productName || "",
        },
        {
            field: "orderDate",
            headerName: "Order Date",
            minWidth: 110,
            flex: 0.45,
            valueGetter: (_value, row) => formatDate(row.orderDate),
        },
        {
            field: "purchaseDate",
            headerName: "Purchase Date",
            minWidth: 120,
            flex: 0.45,
            valueGetter: (_value, row) => formatDate(row.purchaseDate),
        },
    ];

    const columns = layout.applyWidths(baseColumns);

    return (
        <div className="catalog-grid-shell">
            <div className="catalog-toolbar">
                <TextField
                    size="small"
                    placeholder="Search item, shelf, order, customer…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={catalogSearchFieldSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                            </InputAdornment>
                        ),
                    }}
                />
                {selectedIds.length > 0 ? (
                    <span className="catalog-selection-hint">{selectedIds.length} selected</span>
                ) : (
                    <span className="catalog-selection-hint">
                        {filteredRows.length} item{filteredRows.length === 1 ? "" : "s"}
                        {filterActive || searchInput.trim() ? " (filtered)" : ""}
                    </span>
                )}
                {actionError ? (
                    <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                        {actionError}
                    </Typography>
                ) : null}
                <div className="catalog-toolbar-spacer" />
                <GridLayoutControls layout={layout} columns={baseColumns} />
                <Button
                    size="small"
                    variant="outlined"
                    disabled={selectedIds.length !== 1 || marking}
                    startIcon={
                        marking ? (
                            <CircularProgress size={14} />
                        ) : (
                            <ReportProblemOutlinedIcon fontSize="small" />
                        )
                    }
                    onClick={() => markMissing(selectedIds[0])}
                    sx={{
                        ...catalogQuietButtonSx,
                        color: "#b45309",
                        borderColor: "#fcd34d",
                        backgroundColor: "#fffbeb",
                        "&:hover": {
                            borderColor: "#f59e0b",
                            backgroundColor: "#fef3c7",
                        },
                    }}
                >
                    Mark missing
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={!selectedIds.length || marking}
                    startIcon={
                        marking ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : (
                            <CheckCircleOutlineOutlinedIcon fontSize="small" />
                        )
                    }
                    onClick={() => markPicked(selectedIds)}
                    sx={catalogPrimaryButtonSx}
                >
                    Mark picked
                </Button>
            </div>
            <GridFilterBar {...filterBar} />
            {!loading && !(rows || []).length ? (
                <Typography sx={{ px: 2, py: 2, color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
                    No open pick items. Pull orders to assign Listed units, or everything here is
                    already marked picked.
                </Typography>
            ) : null}
            <div className="catalog-grid-main">
                <DataGrid
                    {...appDataGridDefaultProps}
                    loading={Boolean(loading || marking)}
                    rows={filteredRows}
                    columns={columns}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={setSelectionModel}
                    sortModel={layout.sortModel}
                    onSortModelChange={layout.onSortModelChange}
                    columnVisibilityModel={layout.columnVisibilityModel}
                    onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                    onColumnWidthChange={layout.onColumnWidthChange}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    slotProps={appDataGridSlotProps}
                    sx={catalogDataGridSx}
                />
            </div>
        </div>
    );
}
